/**
 * Slice 2E1 · Levi AI Estimate (Foundation)
 * =========================================
 *
 * Guided single-page flow for lightweight AI-recommended estimates.
 * Reuses:
 *   - Voice recording pattern from VoiceNotes.jsx (MediaRecorder → upload)
 *   - Photo upload pattern (multipart)
 *   - useLang() for full EN/ES localization of the contractor-facing UI
 *
 * State machine mirrors the backend session status:
 *   collecting  → analyzing  → recommended  → used | cancelled
 *
 * See /app/backend/levi_estimate.py for the corresponding API.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import LeviVisionPanel from "@/components/levi/LeviVisionPanel";
import LeviWorkingEnvironment from "@/components/levi/LeviWorkingEnvironment";
import VideoWalkRecorder from "@/components/videoWalk/VideoWalkRecorder";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import {
  Camera, Mic, Square, Upload, Loader2, Trash2, ArrowLeft, Sparkles,
  Send, PencilLine, RefreshCcw, X, CheckCircle2, Video,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PhotoTile = ({ photo, onDelete, deleteLabel }) => (
  <div className="relative group border border-[var(--jc-border)] rounded-md p-3 bg-[var(--jc-bg)]" data-testid={`levi-photo-tile-${photo.id}`}>
    <div className="flex items-start gap-2">
      <Camera size={14} className="text-[var(--jc-text-soft)] shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold truncate">{photo.original_filename}</div>
        <div className="text-[10px] text-[var(--jc-text-soft)]">
          {Math.max(1, Math.round((photo.size_bytes || 0) / 1024))} KB · {photo.mime_type?.split("/")[1]?.toUpperCase()}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDelete(photo.id)}
        data-testid={`levi-photo-delete-${photo.id}`}
        aria-label={deleteLabel}
        className="opacity-70 hover:opacity-100 text-red-600 hover:bg-red-50 rounded p-1"
      >
        <Trash2 size={14} />
      </button>
    </div>
  </div>
);

const VoiceTile = ({ voice, t }) => {
  const statusLabel = {
    uploaded: t("levi_estimate.voice_status_uploaded"),
    transcribing: t("levi_estimate.voice_status_transcribing"),
    transcribed: t("levi_estimate.voice_status_transcribed"),
    failed: t("levi_estimate.voice_status_failed"),
  }[voice.status] || voice.status;
  return (
    <div className="border border-[var(--jc-border)] rounded-md p-3" data-testid={`levi-voice-tile-${voice.id}`}>
      <div className="flex items-center gap-2 text-xs">
        <Mic size={14} className="text-[var(--jc-text-soft)] shrink-0" />
        <span className="font-semibold truncate flex-1">{voice.original_filename}</span>
        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
          voice.status === "transcribed" ? "bg-green-100 text-green-800"
          : voice.status === "failed" ? "bg-red-100 text-red-800"
          : "bg-yellow-100 text-yellow-800"
        }`}>
          {statusLabel}
        </span>
      </div>
      {voice.transcript && (
        <p className="mt-2 text-xs text-[var(--jc-text)] leading-relaxed whitespace-pre-wrap">{voice.transcript}</p>
      )}
      {voice.transcription_error && (
        <p className="mt-2 text-xs text-red-700">{voice.transcription_error}</p>
      )}
    </div>
  );
};

const LeviEstimate = () => {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  // Vision Three-Stitch (c) · Frontend surface routing (Feb 2026).
  // When the unified Levi conversation returns
  //   surface = "video_walk_recorder_session_scoped"
  // with an existing levi_estimate_session_id, `useLeviCommand.js`
  // navigates the contractor to
  //   /app/estimates/new/levi?levi_camera=1&lsid=<lsid>
  // We honor those params here to (1) reuse the pre-created session
  // instead of double-creating one, (2) skip the "Before You Continue"
  // gate (contractor already expressed explicit Vision intent), and
  // (3) auto-open the VideoWalkRecorder + scroll it into view.
  const [searchParams] = useSearchParams();
  const _lsidFromUrl = searchParams.get("lsid") || "";
  const _autoCamera = searchParams.get("levi_camera") === "1";

  // Slice 2E1 UX refinement · "Before You Continue" gate. Shown on first
  // mount so the contractor consciously acknowledges the intended
  // use-case for one-off work. Choosing "Go to Full Project Estimate"
  // routes them back to /app/estimates/new; "Continue with Levi" opens
  // the guided flow. Skipped when the contractor arrives via an
  // explicit Vision intent (levi_camera=1) — they've already chosen.
  const [beforeOpen, setBeforeOpen] = useState(!_autoCamera);
  // Talk It Through hero CTA — opens the unified One-Levi drawer in its
  // voice-first "command" mode. Because <DashboardLayout> owns the
  // LeviDrawerProvider (LeviDrawerProvider wraps DashboardLayout in
  // components/layout/DashboardLayout.jsx L.538-542), the useLeviDrawer
  // hook cannot be called from THIS component — we're the parent of
  // the provider at render time. Instead we dispatch the existing
  // `levi:auto-open-drawer` window event that LeviAssistantDrawer
  // listens for (see LeviLauncher.jsx L.84-92) — the handler there
  // sets initialMode="command" and setOpen(true), byte-equivalent to
  // openDrawer("command"). No new session is created. No new API is
  // introduced. No mic auto-start.
  const openLeviTalk = () => {
    setBeforeOpen(false);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("levi:auto-open-drawer"));
    }
  };
  // Smart-redirect state: when GPT tags the analysis as a full-trade
  // project, gate the recommendation UI until the contractor either
  // switches back or explicitly chooses to continue.
  const [acknowledgedFullTrade, setAcknowledgedFullTrade] = useState(false);

  const [session, setSession] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [description, setDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  const [followUpAnswer, setFollowUpAnswer] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [recording, setRecording] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(false);
  // V-P1 · Custom Job with Levi — Vision CTA (Option B).
  // `visionOpen` toggles an inline VideoWalkRecorder mounted in
  // session-scoped mode (project_id=None at capture time). Existing
  // Video Walk capture, upload, transcription, analysis, and Levi
  // review flows are reused unchanged. Evidence continuity into the
  // resulting project happens on `POST /levi-estimate/sessions/:id/use`.
  const [visionOpen, setVisionOpen] = useState(false);

  const [editPriceOpen, setEditPriceOpen] = useState(false);
  const [editPriceValue, setEditPriceValue] = useState("");
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseNote, setReviseNote] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [useOpen, setUseOpen] = useState(false);
  const [useForm, setUseForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    job_address: "", amount: "",
  });
  const [submittingUse, setSubmittingUse] = useState(false);

  const photoInputRef = useRef(null);
  const voiceInputRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  // Create the session lazily on first mount. When the contractor
  // arrived via an explicit conversational Vision intent
  // (?lsid=<lsid>), reuse that pre-created Levi Estimate session
  // instead of double-creating one. Falls back to POST /sessions if
  // the lsid can't be loaded (stale link, cross-tenant, etc.).
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (session || creatingSession) return;
      setCreatingSession(true);
      try {
        if (_lsidFromUrl) {
          try {
            const { data } = await api.get(`/levi-estimate/sessions/${_lsidFromUrl}`);
            if (!ignore) setSession(data);
            return;
          } catch {
            // fall through to a fresh create
          }
        }
        const { data } = await api.post("/levi-estimate/sessions", {});
        if (!ignore) setSession(data);
      } catch {
        toast.error(t("levi_estimate.toast_analyze_failed"));
      } finally {
        if (!ignore) setCreatingSession(false);
      }
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vision Three-Stitch (a) · Auto-open + scroll to the recorder once
  // the session is ready when either (i) the contractor arrived from
  // an explicit Vision intent (levi_camera=1), or (ii) they clicked
  // the top hero CTA before the session had finished creating (we set
  // visionOpen=true, but the recorder can't render until session
  // exists — this effect surfaces the recorder as soon as it can).
  // Uses the shared anchor `data-testid=levi-vision-anchor` so the
  // contractor's viewport lands directly on the recorder.
  useEffect(() => {
    if (!session) return;
    if (!(visionOpen || _autoCamera)) return;
    if (_autoCamera) setVisionOpen(true);
    // Give React a beat to mount the recorder before scrolling.
    const t = setTimeout(() => {
      try {
        const el = document.querySelector('[data-testid="levi-vision-cta-panel"]');
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch { /* noop */ }
    }, 120);
    return () => clearTimeout(t);
  }, [session, visionOpen, _autoCamera]);

  // Local mirror of description so typing feels instant. Persists on blur.
  const persistDescription = async () => {
    if (!session) return;
    if ((description || "") === (session.description || "")) return;
    setSavingDescription(true);
    try {
      const { data } = await api.patch(`/levi-estimate/sessions/${session.id}`, { description });
      setSession(data);
    } finally {
      setSavingDescription(false);
    }
  };
  useEffect(() => {
    if (session && description === "" && (session.description || "")) {
      setDescription(session.description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  const uploadPhoto = async (file) => {
    if (!session || !file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingPhoto(true);
    try {
      const { data } = await api.post(`/levi-estimate/sessions/${session.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSession(data);
      toast.success(t("levi_estimate.toast_photo_uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("levi_estimate.toast_photo_failed"));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const deletePhoto = async (pid) => {
    if (!session) return;
    try {
      const { data } = await api.delete(`/levi-estimate/sessions/${session.id}/photos/${pid}`);
      setSession(data);
      toast.success(t("levi_estimate.toast_photo_deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("levi_estimate.toast_photo_failed"));
    }
  };

  const uploadVoice = async (file, source) => {
    if (!session || !file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingVoice(true);
    try {
      const { data } = await api.post(
        `/levi-estimate/sessions/${session.id}/voice-notes?source=${source || "uploaded"}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSession(data);
      toast.success(t("levi_estimate.toast_voice_uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("levi_estimate.toast_voice_failed"));
    } finally {
      setUploadingVoice(false);
    }
  };

  const startRecording = async () => {
    if (recording) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error(t("levi_estimate.toast_voice_failed"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try {
          streamRef.current?.getTracks()?.forEach((tr) => tr.stop());
        } catch {
          /* noop */
        }
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const ext = (rec.mimeType || "audio/webm").includes("webm") ? "webm" : "m4a";
        const f = new File([blob], `levi-recording-${Date.now()}.${ext}`, { type: rec.mimeType || "audio/webm" });
        await uploadVoice(f, "recorded");
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error(t("levi_estimate.toast_voice_failed"));
    }
  };
  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  };

  const canAnalyze = useMemo(() => {
    if (!session) return false;
    const hasDescription = (description || "").trim().length >= 3;
    const hasPhoto = (session.photos || []).length > 0;
    const hasVoice = (session.voice_notes || []).some((v) => (v.transcript || "").trim().length > 0);
    return hasDescription || hasPhoto || hasVoice;
  }, [session, description]);

  const runAnalyze = async ({ revisionNote } = {}) => {
    if (!session) return;
    // Persist description first so the backend sees the latest text.
    if ((description || "") !== (session.description || "")) {
      await persistDescription();
    }
    setAnalyzing(true);
    setAnalyzeError(false);
    try {
      const { data } = await api.post(`/levi-estimate/sessions/${session.id}/analyze`, {
        revision_note: revisionNote || null,
        app_language: lang,
      });
      setSession(data);
      // Any new analysis resets the smart-redirect acknowledgement so a
      // freshly-flagged full-trade result gets the banner again.
      setAcknowledgedFullTrade(false);
    } catch (e) {
      setAnalyzeError(true);
      toast.error(e?.response?.data?.detail || t("levi_estimate.toast_analyze_failed"));
    } finally {
      setAnalyzing(false);
    }
  };

  const saveAnswer = async () => {
    if (!session) return;
    try {
      const { data } = await api.patch(`/levi-estimate/sessions/${session.id}`, {
        follow_up_answer: followUpAnswer,
      });
      setSession(data);
      toast.success(t("levi_estimate.toast_answer_saved"));
    } catch {
      toast.error(t("levi_estimate.toast_analyze_failed"));
    }
  };

  const openUseDialog = () => {
    if (!session?.recommendation) return;
    setUseForm({
      customer_name: "", customer_email: "", customer_phone: "",
      job_address: "",
      amount: String(session.recommendation.amount || ""),
    });
    setUseOpen(true);
  };

  const submitUse = async () => {
    if (!session) return;
    if (!useForm.customer_name.trim()) {
      toast.error(t("levi_estimate.use_missing_customer"));
      return;
    }
    setSubmittingUse(true);
    try {
      const { data } = await api.post(`/levi-estimate/sessions/${session.id}/use`, {
        amount: parseFloat(useForm.amount || "0"),
        customer_name: useForm.customer_name.trim(),
        customer_email: useForm.customer_email.trim(),
        customer_phone: useForm.customer_phone.trim(),
        job_address: useForm.job_address.trim(),
      });
      toast.success(t("levi_estimate.toast_used"));
      setUseOpen(false);
      navigate(`/app/estimates/${data.estimate_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("levi_estimate.toast_use_failed"));
    } finally {
      setSubmittingUse(false);
    }
  };

  const submitEditPrice = () => {
    const n = parseFloat(editPriceValue);
    if (isNaN(n) || n <= 0) return;
    setSession((prev) => prev ? ({
      ...prev,
      recommendation: { ...(prev.recommendation || {}), amount: n },
    }) : prev);
    setEditPriceOpen(false);
  };

  const submitRevise = async () => {
    const note = reviseNote.trim();
    if (!note) return;
    setReviseOpen(false);
    setReviseNote("");
    await runAnalyze({ revisionNote: note });
  };

  const doCancel = async () => {
    if (!session) return;
    try {
      await api.delete(`/levi-estimate/sessions/${session.id}`);
    } catch {
      /* best-effort */
    }
    toast.success(t("levi_estimate.toast_cancelled"));
    navigate("/app/estimates/new");
  };

  const status = session?.status || "collecting";
  const showRecommendation = status === "recommended" && !!session?.recommendation;
  const rec = session?.recommendation || {};
  const isFullTradeSuspect = !!rec.looks_like_full_trade_project;
  const showSmartRedirect = showRecommendation && isFullTradeSuspect && !acknowledgedFullTrade;
  const showRecommendationBody = showRecommendation && (!isFullTradeSuspect || acknowledgedFullTrade);

  // Reusable Levi Working Environment view-model adapter.
  //
  // Flattens the current session + local UI flags into the adapter-
  // derived flag set that the Working Environment's pure state
  // module consumes. The seven-key recommendation contract is read
  // ONLY inside this adapter (and inside the existing recommendation
  // section below); the Working Environment itself never references
  // those keys. The four-key reasoning contract is not touched here
  // — it flows only through the locked LeviVisionPanel.
  const workingEnvViewModel = useMemo(() => {
    const recForAdapter = session?.recommendation || null;
    const hasRecommendation = !!recForAdapter;
    const pendingFollowUpQuestion =
      recForAdapter && typeof recForAdapter.follow_up_question === "string"
        ? recForAdapter.follow_up_question.trim()
        : "";
    const hasPendingFollowUp =
      hasRecommendation && !!pendingFollowUpQuestion && !followUpAnswer.trim();
    const fullTradeUnacknowledged =
      hasRecommendation && !!recForAdapter.looks_like_full_trade_project && !acknowledgedFullTrade;
    const hasContractorInput =
      !!description.trim() ||
      (session?.photos?.length || 0) > 0 ||
      (session?.voice_notes || []).some((v) => (v?.transcript || "").trim().length > 0) ||
      !!followUpAnswer.trim();
    return {
      status,
      flags: {
        analyzing,
        analyzeError,
        acknowledgedFullTrade,
        hasBeenUsed: status === "used",
        hasRecommendation,
        hasPendingFollowUp,
        fullTradeUnacknowledged,
        hasContractorInput,
      },
    };
  }, [session, description, followUpAnswer, analyzing, analyzeError, acknowledgedFullTrade, status]);


  return (
    <DashboardLayout
      title={t("levi_estimate.page_title")}
      subtitle={t("levi_estimate.page_subtitle")}
      actions={
        <Link
          to="/app/estimates/new"
          data-testid="levi-back-to-new-estimate"
          className="text-sm text-[var(--jc-text-soft)] inline-flex items-center gap-1 hover:text-[var(--jc-text)]"
        >
          <ArrowLeft size={14} /> <span className="hidden sm:inline">{t("levi_estimate.back_to_new_estimate")}</span>
        </Link>
      }
    >
      <div className="max-w-5xl mx-auto space-y-6" data-testid="levi-estimate-page">
        {/* V-P2 · Custom Job with Levi — approved full-width hero (2026-08-22).
            Single approved artwork spans the full hero width. Native
            HTML overlay sits over the intentional open sky area on the
            right. Mirror of the Dashboard hero pattern: one artwork,
            one overlay, no baked-in UI. */}
        <section
          className="relative overflow-hidden rounded-2xl border border-[var(--jc-border)] bg-white shadow-sm"
          data-testid="levi-estimate-vp2-hero"
        >
          {/* Desktop artwork (fills the hero) */}
          <img
            src="/brand/custom_job/hero.png"
            alt={t("levi_estimate.hero_image_alt")}
            className="hidden lg:block w-full h-auto select-none"
            draggable={false}
            data-testid="levi-estimate-hero-img"
          />
          {/* Mobile stacked artwork — portrait asset renders natively without crop */}
          <img
            src="/brand/custom_job/hero_mobile.png"
            alt=""
            className="block lg:hidden w-full h-auto select-none"
            draggable={false}
            aria-hidden="true"
            data-testid="levi-estimate-hero-img-mobile"
          />

          {/* Desktop native HTML overlay on the RIGHT half of the artwork */}
          <div className="hidden lg:flex absolute inset-0 items-start justify-end pointer-events-none pt-8 xl:pt-10">
            <div className="w-[46%] max-w-[560px] mr-[3%] pointer-events-auto">
              <div
                className="text-sm font-extrabold tracking-[0.2em] uppercase text-[var(--jc-blue)] mb-3"
                data-testid="levi-estimate-hero-eyebrow"
              >
                {t("levi_estimate.hero_eyebrow")}
              </div>
              <h1
                className="font-display text-4xl xl:text-5xl leading-tight text-[var(--jc-text)]"
                data-testid="levi-estimate-hero-headline"
              >
                {t("levi_estimate.hero_headline")}
              </h1>
              <p className="mt-4 text-lg xl:text-xl font-medium text-[var(--jc-text)] leading-relaxed">
                {t("levi_estimate.hero_body")}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => { setBeforeOpen(false); setVisionOpen(true); }}
                  disabled={!session}
                  data-testid="levi-estimate-hero-vision-btn"
                  className="jc-btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-md text-base font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                  title={session ? undefined : (t("levi_estimate.vision_preparing") || "Preparing…")}
                >
                  <Video size={16} /> {t("levi_estimate.hero_primary_cta")}
                </button>
                <button
                  type="button"
                  onClick={openLeviTalk}
                  data-testid="levi-estimate-hero-talk-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 text-base font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white/80 hover:bg-white"
                >
                  <Mic size={16} /> {t("levi_estimate.hero_talk_cta")}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile stacked overlay copy */}
          <div className="lg:hidden p-5" data-testid="levi-estimate-hero-mobile-copy">
            <div className="text-xs font-extrabold tracking-[0.2em] uppercase text-[var(--jc-blue)] mb-2">
              {t("levi_estimate.hero_eyebrow")}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl leading-tight text-[var(--jc-text)]">
              {t("levi_estimate.hero_headline")}
            </h1>
            <p className="mt-3 text-base font-medium text-[var(--jc-text)] leading-relaxed">
              {t("levi_estimate.hero_body")}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => { setBeforeOpen(false); setVisionOpen(true); }}
                disabled={!session}
                data-testid="levi-estimate-hero-vision-btn-mobile"
                className="jc-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Video size={14} /> {t("levi_estimate.hero_primary_cta")}
              </button>
              <button
                type="button"
                onClick={openLeviTalk}
                data-testid="levi-estimate-hero-talk-btn-mobile"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white hover:bg-[var(--jc-bg)]"
              >
                <Mic size={14} /> {t("levi_estimate.hero_talk_cta")}
              </button>
            </div>
          </div>
        </section>

        {/* V-P2 · Three-step "How it works" (replaces prior four-card block) */}
        <section className="jc-card p-6 md:p-8" data-testid="levi-estimate-how-it-works">
          <div className="overline text-center text-[var(--jc-text-soft)]">
            {t("levi_estimate.how_overline")}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            {[
              { key: 1, t: t("levi_estimate.how_step1_title"), b: t("levi_estimate.how_step1_body") },
              { key: 2, t: t("levi_estimate.how_step2_title"), b: t("levi_estimate.how_step2_body") },
              { key: 3, t: t("levi_estimate.how_step3_title"), b: t("levi_estimate.how_step3_body") },
            ].map((s) => (
              <div key={s.key} data-testid={`levi-how-step-${s.key}`}>
                <div className="w-8 h-8 rounded-full bg-[var(--jc-blue)] text-white font-bold flex items-center justify-center text-sm mb-3">
                  {s.key}
                </div>
                <div className="font-semibold text-base text-[var(--jc-text)]">{s.t}</div>
                <p className="text-sm text-[var(--jc-text-soft)] mt-1 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[var(--jc-text-soft)] italic mt-6" data-testid="levi-how-footer">
            {t("levi_estimate.how_footer")}
          </p>
        </section>

        {/* V-P2 · Contractor-review safety panel */}
        <div
          className="rounded-md border border-[var(--jc-blue)]/30 bg-[var(--jc-blue)]/5 px-4 py-3 flex items-start gap-3"
          data-testid="levi-estimate-privacy-message"
        >
          <Sparkles size={16} className="text-[var(--jc-blue)] mt-0.5 shrink-0" />
          <p className="text-sm text-[var(--jc-text)]">{t("levi_estimate.privacy_message")}</p>
        </div>

        <LeviWorkingEnvironment
          viewModel={workingEnvViewModel}
          visualRegion={null}
          hideIdentity={true}
          interactionRegion={
        /* Step 1 — Collect inputs (heading is intentionally the first
            thing the contractor sees on this page). */
        <section className="jc-card p-6 space-y-5">
          <div>
            <h2 className="font-display text-3xl">{t("levi_estimate.step1_heading")}</h2>
            <p className="text-sm text-[var(--jc-text-soft)] mt-2">{t("levi_estimate.step1_help")}</p>
          </div>

          {/* V-P1 · Turn on Vision & walk Levi through it — session-scoped Video Walk (Option B). */}
          <div
            className="rounded-lg border border-[var(--jc-blue)]/30 bg-gradient-to-br from-[var(--jc-blue)]/8 to-transparent p-4"
            data-testid="levi-vision-cta-panel"
          >
            {!visionOpen ? (
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-11 h-11 rounded-full bg-[var(--jc-blue)]/12 text-[var(--jc-blue)] flex items-center justify-center">
                  <Video size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{t("levi_estimate.vision_cta_title")}</div>
                  <p className="text-xs text-[var(--jc-text-soft)] mt-1">
                    {t("levi_estimate.vision_cta_body")}
                  </p>
                  <button
                    type="button"
                    onClick={() => setVisionOpen(true)}
                    disabled={!session}
                    data-testid="levi-vision-cta-btn"
                    className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold jc-btn-blue disabled:opacity-60"
                  >
                    <Video size={14} /> {t("levi_estimate.vision_cta_button")}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm inline-flex items-center gap-2">
                    <Video size={14} className="text-[var(--jc-blue)]" />
                    {t("levi_estimate.vision_cta_title")}
                  </div>
                  <button
                    type="button"
                    onClick={() => setVisionOpen(false)}
                    data-testid="levi-vision-close-btn"
                    className="text-xs text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] inline-flex items-center gap-1"
                  >
                    <X size={12} /> {t("levi_estimate.vision_hide")}
                  </button>
                </div>
                {session ? (
                  <VideoWalkRecorder
                    leviEstimateSessionId={session.id}
                    autoStart={_autoCamera}
                    embedded
                  />
                ) : (
                  <div className="text-xs text-[var(--jc-text-soft)] inline-flex items-center gap-2">
                    <Loader2 size={12} className="animate-spin" />
                    {t("levi_estimate.vision_preparing")}
                  </div>
                )}
                <p className="text-[11px] text-[var(--jc-text-soft)] italic mt-3">
                  {t("levi_estimate.vision_continuity_hint")}
                </p>
              </div>
            )}
          </div>

          <div className="rounded-md bg-[var(--jc-bg)] p-3 text-xs text-[var(--jc-text-soft)] flex items-start gap-2">
            <Sparkles size={14} className="text-[var(--jc-blue)] mt-0.5 shrink-0" />
            <span>{t("levi_estimate.internal_note")}</span>
          </div>

          {/* Photos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline">{t("levi_estimate.section_photos")}</div>
              <span className="text-[11px] text-[var(--jc-text-soft)]">{t("levi_estimate.section_photos_hint")}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto || !session}
                data-testid="levi-photos-upload-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60"
              >
                {uploadingPhoto ? <Loader2 className="animate-spin" size={14} /> : <Camera size={14} />}
                {t("levi_estimate.add_photos_btn")}
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  for (const f of files) await uploadPhoto(f);
                  if (photoInputRef.current) photoInputRef.current.value = "";
                }}
                className="hidden"
                data-testid="levi-photos-file-input"
              />
            </div>
            {session?.photos?.length ? (
              <div className="grid sm:grid-cols-2 gap-2" data-testid="levi-photos-list">
                {session.photos.map((p) => (
                  <PhotoTile key={p.id} photo={p} onDelete={deletePhoto} deleteLabel={t("levi_estimate.delete_photo")} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-[var(--jc-text-soft)]" data-testid="levi-photos-empty">
                {t("levi_estimate.empty_state_photos")}
              </p>
            )}
          </div>

          {/* Voice notes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline">{t("levi_estimate.section_voice")}</div>
              <span className="text-[11px] text-[var(--jc-text-soft)]">{t("levi_estimate.section_voice_hint")}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {!recording ? (
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={uploadingVoice || !session}
                  data-testid="levi-voice-record-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                >
                  <Mic size={14} /> {t("levi_estimate.record_btn")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopRecording}
                  data-testid="levi-voice-stop-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md bg-red-600 text-white hover:bg-red-700"
                >
                  <Square size={14} /> {t("levi_estimate.stop_btn")}
                </button>
              )}
              <button
                type="button"
                onClick={() => voiceInputRef.current?.click()}
                disabled={uploadingVoice || recording || !session}
                data-testid="levi-voice-upload-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60"
              >
                {uploadingVoice ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                {t("levi_estimate.upload_audio_btn")}
              </button>
              <input
                ref={voiceInputRef}
                type="file"
                accept="audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/wav,audio/x-wav,audio/webm,audio/ogg"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (f) await uploadVoice(f, "uploaded");
                  if (voiceInputRef.current) voiceInputRef.current.value = "";
                }}
                className="hidden"
                data-testid="levi-voice-file-input"
              />
            </div>
            {session?.voice_notes?.length ? (
              <div className="space-y-2" data-testid="levi-voice-list">
                {session.voice_notes.map((v) => <VoiceTile key={v.id} voice={v} t={t} />)}
              </div>
            ) : (
              <p className="text-xs text-[var(--jc-text-soft)]" data-testid="levi-voice-empty">
                {t("levi_estimate.empty_state_voice")}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline">{t("levi_estimate.section_description")}</div>
              {savingDescription && <span className="text-[10px] text-[var(--jc-text-soft)]"><Loader2 className="inline animate-spin" size={10}/></span>}
            </div>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={persistDescription}
              placeholder={t("levi_estimate.description_placeholder")}
              data-testid="levi-description-textarea"
              className="w-full text-sm rounded-md border border-[var(--jc-border-strong)] px-3 py-2"
            />
          </div>

          {/* Follow-up answer (only rendered when Levi asked something) */}
          {session?.follow_up_question && (
            <div className="rounded-md border border-[var(--jc-blue)]/30 bg-[var(--jc-blue)]/5 p-4">
              <div className="overline text-[var(--jc-blue)] mb-1">{t("levi_estimate.section_followup")}</div>
              <p className="text-sm font-semibold mb-2" data-testid="levi-followup-question">{session.follow_up_question}</p>
              <textarea
                rows={2}
                value={followUpAnswer}
                onChange={(e) => setFollowUpAnswer(e.target.value)}
                placeholder={t("levi_estimate.followup_answer_placeholder")}
                data-testid="levi-followup-answer"
                className="w-full text-sm rounded-md border border-[var(--jc-border-strong)] px-3 py-2 bg-white"
              />
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={saveAnswer}
                  disabled={!followUpAnswer.trim()}
                  data-testid="levi-save-answer-btn"
                  className="px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-50"
                >
                  {t("levi_estimate.save_answer")}
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[var(--jc-border)]">
            <button
              type="button"
              onClick={() => runAnalyze()}
              disabled={!canAnalyze || analyzing || !session}
              data-testid="levi-analyze-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-md jc-btn-blue disabled:opacity-60"
            >
              {analyzing ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
              {analyzing ? t("levi_estimate.analyzing") : t("levi_estimate.analyze_btn")}
            </button>
            {!canAnalyze && !analyzing && (
              <span className="text-xs text-[var(--jc-text-soft)]">{t("levi_estimate.analyze_disabled_hint")}</span>
            )}
          </div>
        </section>
          }
          smartRedirectRegion={showSmartRedirect ? (
        /* Smart redirect · GPT-flagged full-trade project */
        <section className="jc-card p-6 border-2 border-amber-300 bg-amber-50" data-testid="levi-smart-redirect">
            <div className="flex items-start gap-3">
              <Sparkles size={18} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-display text-lg text-amber-900" data-testid="levi-smart-title">{t("levi_estimate.smart_title")}</div>
                <p className="text-sm text-amber-900/90 mt-1">{t("levi_estimate.smart_body")}</p>
                {rec.full_trade_reason && (
                  <p className="text-xs text-amber-800 italic mt-2" data-testid="levi-smart-reason">{rec.full_trade_reason}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => navigate("/app/estimates/new")}
                    data-testid="levi-smart-switch"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary"
                  >
                    {t("levi_estimate.smart_switch")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAcknowledgedFullTrade(true)}
                    data-testid="levi-smart-continue"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-amber-400 text-amber-900 hover:bg-amber-100"
                  >
                    {t("levi_estimate.smart_continue")}
                  </button>
                </div>
              </div>
            </div>
          </section>
          ) : null}
          recommendationRegion={showRecommendationBody ? (
        /* Step 3 — Recommendation. Grounding & Trust Guardrails
            (2026-08-08): renders a RANGE with an optional suggested
            working price; supports the three status branches
            (recommended · needs_more_information · cannot_responsibly_price).
            Contractor remains the final pricing authority. */
        <section className="jc-card p-6 space-y-4" data-testid="levi-recommendation">
            <div className="flex items-start gap-2 text-[var(--jc-navy)]">
              <Sparkles size={16} className="text-[var(--jc-blue)] mt-1 shrink-0" />
              <div className="font-display text-lg leading-snug" data-testid="levi-recommendation-heading">
                {t("levi_estimate.recommendation_heading")}
              </div>
            </div>
            {session.recommendation.status === "needs_more_information" ? (
              <div className="pl-6" data-testid="levi-needs-more-info">
                <div className="overline mb-1 text-[var(--jc-blue)]">
                  {t("levi_estimate.needs_more_info_overline")}
                </div>
                <p className="text-base font-semibold text-[var(--jc-navy)]">
                  {session.recommendation.follow_up_question || t("levi_estimate.needs_more_info_generic")}
                </p>
                <p className="text-xs text-[var(--jc-text-soft)] mt-2 max-w-xl">
                  {t("levi_estimate.needs_more_info_hint")}
                </p>
              </div>
            ) : session.recommendation.status === "cannot_responsibly_price" ? (
              <div className="pl-6" data-testid="levi-cannot-price">
                <div className="overline mb-1 text-[var(--jc-yellow)]">
                  {t("levi_estimate.cannot_price_overline")}
                </div>
                <p className="text-base font-semibold text-[var(--jc-navy)]">
                  {t("levi_estimate.cannot_price_headline")}
                </p>
                <p className="text-sm text-[var(--jc-text-soft)] mt-2 max-w-xl">
                  {session.recommendation.explanation || t("levi_estimate.cannot_price_body")}
                </p>
              </div>
            ) : (
              <>
                <div className="pl-6" data-testid="levi-recommendation-range">
                  <div className="overline mb-1">{t("levi_estimate.recommended_range_label")}</div>
                  <div className="font-mono-jc text-3xl sm:text-4xl font-bold text-[var(--jc-navy)]">
                    <span data-testid="levi-recommendation-range-low">
                      {money(session.recommendation.amount_low || session.recommendation.amount || 0)}
                    </span>
                    <span className="mx-2 text-[var(--jc-text-soft)] font-normal">–</span>
                    <span data-testid="levi-recommendation-range-high">
                      {money(session.recommendation.amount_high || session.recommendation.amount || 0)}
                    </span>
                  </div>
                </div>
                {(session.recommendation.suggested_working_price != null || session.recommendation.amount != null) && (
                  <div className="pl-6 mt-2" data-testid="levi-suggested-working-price">
                    <div className="overline mb-1">{t("levi_estimate.suggested_working_price_label")}</div>
                    <div className="font-mono-jc text-2xl font-bold text-[var(--jc-blue)]">
                      {money(session.recommendation.suggested_working_price ?? session.recommendation.amount)}
                    </div>
                    <p className="text-[11px] text-[var(--jc-text-soft)] mt-1 max-w-xl">
                      {t("levi_estimate.recommendation_hint")}
                    </p>
                  </div>
                )}
                {Array.isArray(session.recommendation.drivers) && session.recommendation.drivers.length > 0 && (
                  <div className="pl-6" data-testid="levi-recommendation-drivers">
                    <div className="overline mb-1">{t("levi_estimate.drivers_label")}</div>
                    <ul className="text-sm space-y-0.5 text-[var(--jc-text)]">
                      {session.recommendation.drivers.map((d, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="text-[var(--jc-blue)] mt-0.5">•</span>
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            <p className="text-xs text-[var(--jc-text-soft)] leading-relaxed max-w-xl pl-6">
              {t("levi_estimate.recommendation_note")}
            </p>

            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="overline mb-1">{t("levi_estimate.job_type_label")}</div>
                <div className="font-semibold" data-testid="levi-job-type">{session.recommendation.job_type}</div>
              </div>
              <div>
                <div className="overline mb-1">{t("levi_estimate.scope_summary_label")}</div>
                <p data-testid="levi-scope-summary" className="whitespace-pre-wrap">{session.recommendation.scope_summary}</p>
              </div>
            </div>
            {session.recommendation.explanation && session.recommendation.status === "recommended" && (
              <div className="text-sm">
                <div className="overline mb-1">{t("levi_estimate.explanation_label")}</div>
                <p data-testid="levi-explanation" className="whitespace-pre-wrap text-[var(--jc-text-soft)]">{session.recommendation.explanation}</p>
              </div>
            )}

            {/* Step 4 — Actions. Use / edit price only shown when Levi
                actually produced a price; the needs_more_information
                and cannot_responsibly_price branches only offer
                Revise (add more info) and Cancel. */}
            <div className="flex flex-wrap gap-2 pt-3 border-t border-[var(--jc-border)]">
              {session.recommendation.status === "recommended" && (
                <>
                  <button
                    type="button"
                    onClick={openUseDialog}
                    data-testid="levi-action-use"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary"
                  >
                    <CheckCircle2 size={14} /> {t("levi_estimate.action_use")}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditPriceValue(String(session.recommendation.suggested_working_price ?? session.recommendation.amount ?? "")); setEditPriceOpen(true); }}
                    data-testid="levi-action-edit-price"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                  >
                    <PencilLine size={14} /> {t("levi_estimate.action_edit_price")}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setReviseOpen(true)}
                data-testid="levi-action-revise"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
              >
                <RefreshCcw size={14} /> {t("levi_estimate.action_revise")}
              </button>
              <button
                type="button"
                onClick={() => setCancelOpen(true)}
                data-testid="levi-action-cancel"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md text-red-600 border border-red-200 hover:bg-red-50"
              >
                <X size={14} /> {t("levi_estimate.action_cancel")}
              </button>
            </div>
          </section>
          ) : null}
          reasoningRegion={
            analyzing && !showRecommendation ? (
              <LeviVisionPanel loading />
            ) : !analyzing && analyzeError && !showRecommendation ? (
              <LeviVisionPanel error />
            ) : showRecommendationBody ? (
              <LeviVisionPanel reasoning={session?.reasoning} />
            ) : null
          }
        />
      </div>

      {/* Before You Continue gate — shown on first mount */}
      <AlertDialog open={beforeOpen} onOpenChange={setBeforeOpen}>
        <AlertDialogContent data-testid="levi-before-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("levi_estimate.before_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("levi_estimate.before_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => navigate("/app/estimates/new")}
              data-testid="levi-before-switch"
            >
              {t("levi_estimate.before_switch")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => setBeforeOpen(false)}
              data-testid="levi-before-continue"
            >
              {t("levi_estimate.before_continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit price dialog */}
      <AlertDialog open={editPriceOpen} onOpenChange={setEditPriceOpen}>
        <AlertDialogContent data-testid="levi-edit-price-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("levi_estimate.edit_price_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("levi_estimate.edit_price_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("levi_estimate.edit_price_input_label")}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={editPriceValue}
              onChange={(e) => setEditPriceValue(e.target.value)}
              data-testid="levi-edit-price-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] font-mono-jc"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="levi-edit-price-cancel">{t("levi_estimate.edit_price_dismiss")}</AlertDialogCancel>
            <AlertDialogAction onClick={submitEditPrice} data-testid="levi-edit-price-confirm">
              {t("levi_estimate.edit_price_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revise dialog */}
      <AlertDialog open={reviseOpen} onOpenChange={setReviseOpen}>
        <AlertDialogContent data-testid="levi-revise-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("levi_estimate.revise_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("levi_estimate.revise_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <textarea
            rows={4}
            value={reviseNote}
            onChange={(e) => setReviseNote(e.target.value)}
            placeholder={t("levi_estimate.revise_placeholder")}
            data-testid="levi-revise-textarea"
            className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="levi-revise-cancel">{t("levi_estimate.revise_dismiss")}</AlertDialogCancel>
            <AlertDialogAction onClick={submitRevise} disabled={!reviseNote.trim()} data-testid="levi-revise-confirm">
              <Send size={14} className="mr-1"/> {t("levi_estimate.revise_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel dialog */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="levi-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("levi_estimate.cancel_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("levi_estimate.cancel_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="levi-cancel-dismiss">{t("levi_estimate.cancel_dismiss")}</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} data-testid="levi-cancel-confirm" className="bg-red-600 hover:bg-red-700">
              {t("levi_estimate.cancel_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Use dialog */}
      <AlertDialog open={useOpen} onOpenChange={setUseOpen}>
        <AlertDialogContent data-testid="levi-use-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("levi_estimate.use_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("levi_estimate.use_dialog_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={t("levi_estimate.use_dialog_customer_name")} testid="levi-use-customer-name"
                   value={useForm.customer_name}
                   onChange={(v) => setUseForm((f) => ({ ...f, customer_name: v }))} />
            <Field label={t("levi_estimate.use_dialog_customer_email")} testid="levi-use-customer-email"
                   value={useForm.customer_email}
                   onChange={(v) => setUseForm((f) => ({ ...f, customer_email: v }))} />
            <Field label={t("levi_estimate.use_dialog_customer_phone")} testid="levi-use-customer-phone"
                   value={useForm.customer_phone}
                   onChange={(v) => setUseForm((f) => ({ ...f, customer_phone: v }))} />
            <Field label={t("levi_estimate.use_dialog_job_address")} testid="levi-use-job-address"
                   value={useForm.job_address}
                   onChange={(v) => setUseForm((f) => ({ ...f, job_address: v }))} />
            <Field label={t("levi_estimate.use_dialog_amount")} testid="levi-use-amount" type="number"
                   value={useForm.amount}
                   onChange={(v) => setUseForm((f) => ({ ...f, amount: v }))} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="levi-use-cancel">{t("levi_estimate.use_dialog_dismiss")}</AlertDialogCancel>
            <AlertDialogAction onClick={submitUse} disabled={submittingUse} data-testid="levi-use-confirm">
              {submittingUse ? <Loader2 className="animate-spin mr-1" size={14}/> : <CheckCircle2 size={14} className="mr-1"/>}
              {t("levi_estimate.use_dialog_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

const Field = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <label className="block text-[11px] font-semibold text-[var(--jc-text-soft)] mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
    />
  </div>
);

export default LeviEstimate;
